import type { CSSProperties } from 'react';

/**
 * T19-03, corrected by T20-01: one height budget for the whole surface, as numbers rather than as
 * four stylesheets, and counting what the surface ACTUALLY puts in the writing column on first run.
 *
 * The bands, the rail and the editor were each given a sensible size on their own, and nobody added
 * them up. Two full guidance bands at 22vh apiece, a rail row with a 180px floor and an editor frame
 * with a 240px floor came to 645px of demands in a window 512px tall; the shell hides its overflow,
 * so what went over the edge was the bottom of the editor and the scrollbar the writer needed to
 * reach the rest of the script.
 *
 * The first round of this budget reserved the writing column's furniture as one round number,
 * `min(96px, 16vh)`, and reserved it against nothing. The furniture that is actually there on first
 * run with media is four paragraphs and a button in a strip that could not shrink, the colour legend
 * above the frame, the strip's own margin and the column's own padding, and none of them read that
 * reserve or was bounded by it. A reserve that nothing obeys is not a budget.
 *
 * So every child of the writing column now declares a bound, the bounds are these numbers, and the
 * reserve is their SUM rather than a guess at it:
 *
 *  * the opening strip is capped at `min(INTRODUCTION_MAX_PX, INTRODUCTION_MAX_VH)` and scrolls
 *    inside itself, so a tall window shows all of it and a short one shows the top of it;
 *  * the legend is capped at `min(LEGEND_MAX_PX, LEGEND_MAX_VH)`;
 *  * the strip's margin and the column's padding are fixed pixels, counted as fixed pixels;
 *  * the editor frame keeps the one floor on this surface, and it bends.
 *
 * The rule the numbers below are chosen against:
 *
 *  * **only the editor has a floor.** Everything else on this surface shrinks and scrolls inside
 *    itself when the window is short: the opening strip, the legend, the guidance bands, the rail,
 *    the drawer. A writer can always reach a bounded box's content by scrolling it; nothing can
 *    reach a clipped editor;
 *  * **the guidance is budgeted together, not one band at a time.** Either band may take up to
 *    `BAND_MAX_VH` of the window, but all of them together never take more than `GUIDANCE_MAX_VH`,
 *    and the rest scroll inside what is left;
 *  * **the floor bends before it breaks.** Each floor is the smaller of a pixel height and a share
 *    of the window, so a short window lowers it rather than pushing the editor off the bottom.
 *
 * The one arithmetic claim, which `heightBudgetFitsAt` states and a test asserts: the declared
 * children of the first-run, media-present surface, added up at a given window height, fit that
 * window. It is a claim about DECLARED values, not a measurement. jsdom does not lay anything out,
 * so no test here can measure a rendered height; what a test can do is add up the numbers, check
 * that the surface carries them, and check that the stylesheets take their bounds from them and the
 * fixed pixels in the stylesheets are the fixed pixels counted here.
 */

/** The window the budget is checked against: the short narrow window the review seat used. */
export const BUDGET_REFERENCE_WIDTH_PX = 1080;

/** The height of that window. Everything below has to fit inside it at once. */
export const BUDGET_REFERENCE_HEIGHT_PX = 512;

/** The most of the window any one guidance band may take on its own. */
export const BAND_MAX_VH = 11;

/** The most of the window every guidance band together may take. */
export const GUIDANCE_MAX_VH = 14;

/** The editor frame's floor in a window with room for it. */
export const EDITOR_FLOOR_PX = 240;

/** The share of a short window the editor frame's floor falls back to. */
export const EDITOR_FLOOR_VH = 40;

/** The opening strip's cap in a window with room for the whole of it. */
export const INTRODUCTION_MAX_PX = 140;

/** The share of a short window the opening strip is capped at, scrolling inside itself. */
export const INTRODUCTION_MAX_VH = 14;

/** The strip's own bottom margin, which a max-height does not include. */
export const INTRODUCTION_GAP_PX = 8;

/** The colour legend's cap in a window with room for it. */
export const LEGEND_MAX_PX = 24;

/** The share of a short window the legend is capped at. */
export const LEGEND_MAX_VH = 4;

/** The writing column's own padding, top and bottom together. Fixed pixels, counted as such. */
export const PANE_PADDING_PX = 22;

/**
 * The rail has no floor. It scrolls inside itself, and in a short window it gives its height back
 * to the script and collapses towards its tab strip, which stays where the writer left it.
 */
export const RAIL_FLOOR_VH = 0;

/** Left for the header and the support drawer, both of which scroll or shrink on their own. */
export const CHROME_RESERVE_VH = 12;

/**
 * T22: the recovery strip's cap in a window with room for it, and its share of a short one.
 *
 * The strip was UNBOUNDED and counted nowhere, so the longest recovery state, every sentence plus
 * both controls, pushed the editor's bottom edge and its scrollbar out of a 1080x512 window: the
 * seat's shots 06 and 07 lost lines that shot 02 had. It is now a child of the budget like every
 * other child of this surface: bounded, scrolling inside itself, with the two controls at the TOP
 * of it so the part a writer can act on is the part that never needs scrolling to.
 *
 * The room for it came from three boxes that already scroll or shrink on their own and each gave
 * some back: the opening strip, the guidance pair and the chrome reserve. Nothing came out of the
 * editor's floor, which is the one floor on this surface and the thing the budget exists to hold.
 */
export const RECOVERY_MAX_PX = 96;

/** The share of a short window the recovery strip is capped at, scrolling inside itself. */
export const RECOVERY_MAX_VH = 8;

/**
 * A bending floor, resolved: the pixel height in a window with room for it, the share of the window
 * in one without. This is what every `min(Npx, Mvh)` in the stylesheets computes to.
 */
export function bendingBoundAt(px: number, vh: number, windowPx: number): number {
  return Math.min(px, (vh * windowPx) / 100);
}

/**
 * What the writing column holds besides the editor frame, on first run with media present: the
 * opening strip at its cap, the strip's margin, the legend at its cap, and the column's padding.
 *
 * Every term is a bound something actually declares, which is the whole correction: the reserve is
 * the sum of the children rather than a number beside them.
 */
export function paneFurnitureAt(windowPx: number): number {
  return bendingBoundAt(INTRODUCTION_MAX_PX, INTRODUCTION_MAX_VH, windowPx)
    + INTRODUCTION_GAP_PX
    + bendingBoundAt(LEGEND_MAX_PX, LEGEND_MAX_VH, windowPx)
    + PANE_PADDING_PX;
}

/**
 * The recovery strip's bound, resolved (T22).
 *
 * It is counted whether or not a recovery state is on the screen, because the budget's claim is
 * about everything the surface CAN insist on at once, and this is one of those things.
 */
export function recoveryBoundAt(windowPx: number): number {
  return bendingBoundAt(RECOVERY_MAX_PX, RECOVERY_MAX_VH, windowPx);
}

/** The floor of the writing column: everything in it, not the frame alone. */
export function scriptColumnFloorAt(windowPx: number): number {
  return bendingBoundAt(EDITOR_FLOOR_PX, EDITOR_FLOOR_VH, windowPx) + paneFurnitureAt(windowPx);
}

/** Every share the surface can insist on at once, in pixels, at a given window height. */
export function heightBudgetTotalAt(windowPx: number): number {
  return scriptColumnFloorAt(windowPx)
    + recoveryBoundAt(windowPx)
    + ((GUIDANCE_MAX_VH + RAIL_FLOOR_VH + CHROME_RESERVE_VH) * windowPx) / 100;
}

/** The same total as a share of the window, for a budget that is spoken of in vh. */
export function heightBudgetTotalVh(windowPx: number = BUDGET_REFERENCE_HEIGHT_PX): number {
  return (heightBudgetTotalAt(windowPx) / windowPx) * 100;
}

/** True while everything the surface can insist on at once fits the window it is a budget for. */
export function heightBudgetFitsAt(windowPx: number): boolean {
  return heightBudgetTotalAt(windowPx) <= windowPx && BAND_MAX_VH <= GUIDANCE_MAX_VH;
}

/**
 * The shortest window the budget fits, in pixels.
 *
 * The shares scale with the window and the fixed pixels do not, so below this height the fixed
 * pixels are more of the window than the budget left them. It is well under any window a writer
 * opens an episode in, and it is stated so that the claim above has a stated edge.
 */
export function heightBudgetFloorPx(): number {
  const fixed = INTRODUCTION_GAP_PX + PANE_PADDING_PX;
  const shares = (EDITOR_FLOOR_VH + INTRODUCTION_MAX_VH + LEGEND_MAX_VH + RECOVERY_MAX_VH
    + GUIDANCE_MAX_VH + RAIL_FLOOR_VH + CHROME_RESERVE_VH) / 100;

  return fixed / (1 - shares);
}

/** True while the budget fits the window the review seat measured the clipping in. */
export function heightBudgetFits(): boolean {
  return heightBudgetFitsAt(BUDGET_REFERENCE_HEIGHT_PX);
}

/**
 * The budget as the custom properties the stylesheets read.
 *
 * They are set on the surface rather than written into the stylesheets so that the numbers have one
 * home and a test can read the same values the layout is using. Each `min()` is what makes a bound
 * bend: the pixel height in a window with room for it, the share of the window in one without. The
 * column's floor is written as the SUM of the properties beside it, so the floor and the children
 * it is the floor of cannot drift apart: change a child's cap and the floor changes with it.
 */
export function heightBudgetStyle(): CSSProperties {
  const fixed = INTRODUCTION_GAP_PX + PANE_PADDING_PX;

  return {
    '--basil-band-max': `${BAND_MAX_VH}vh`,
    '--basil-guidance-max': `${GUIDANCE_MAX_VH}vh`,
    '--basil-editor-floor': `min(${EDITOR_FLOOR_PX}px, ${EDITOR_FLOOR_VH}vh)`,
    '--basil-intro-max': `min(${INTRODUCTION_MAX_PX}px, ${INTRODUCTION_MAX_VH}vh)`,
    '--basil-legend-max': `min(${LEGEND_MAX_PX}px, ${LEGEND_MAX_VH}vh)`,
    '--basil-recovery-max': `min(${RECOVERY_MAX_PX}px, ${RECOVERY_MAX_VH}vh)`,
    '--basil-pane-furniture': 'calc(var(--basil-intro-max) + var(--basil-legend-max) + '
      + `${fixed}px)`,
    '--basil-script-floor': 'calc(var(--basil-editor-floor) + var(--basil-pane-furniture))',
    '--basil-rail-floor': `${RAIL_FLOOR_VH}px`,
  } as CSSProperties;
}
